import React from 'react';
import { User } from 'lucide-react';
import { getProfileImageUrl } from '@/lib/config';

interface AvatarProps {
  imageUrl?: string | null;
  name?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  alt?: string;
  variant?: 'default' | 'dark';
}

const sizeClasses = {
  sm: 'w-6 h-6 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-20 h-20 text-2xl',
};

const iconSizes = {
  sm: 'h-3.5 w-3.5',
  md: 'h-5 w-5',
  lg: 'h-8 w-8',
};

export function Avatar({ imageUrl, name, size = 'md', className = '', alt, variant = 'default' }: AvatarProps) {
  const profileImageUrl = imageUrl ? getProfileImageUrl(imageUrl) : null;
  const [imageError, setImageError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);

  // Get user initials from name
  const getInitials = (name: string | null | undefined): string => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const initials = getInitials(name);
  const showInitials = initials && !profileImageUrl;

  const baseClasses = `rounded-full flex items-center justify-center flex-shrink-0 border-2 ${sizeClasses[size]} ${className}`;
  const isDark = variant === 'dark';
  const borderClass = isDark ? 'border-background/20' : 'border-border';
  const bgClasses = showInitials
    ? isDark
      ? 'bg-primary text-primary-foreground font-semibold'
      : 'bg-primary text-primary-foreground font-semibold'
    : isDark
    ? 'bg-background/20 text-background/70'
    : 'bg-muted text-muted-foreground';

  return (
    <div className={`${baseClasses} ${borderClass} ${bgClasses} relative overflow-hidden`}>
      {profileImageUrl && !imageError ? (
        <>
          {!imageLoaded && (
            <div className="absolute inset-0 bg-muted flex items-center justify-center">
              <User className={`${iconSizes[size]} text-muted-foreground animate-pulse`} />
            </div>
          )}
          <img
            src={profileImageUrl}
            alt={alt || name || 'Profile'}
            className={`absolute inset-0 w-full h-full object-cover ${imageLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-200`}
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              setImageError(true);
              setImageLoaded(false);
            }}
          />
        </>
      ) : showInitials ? (
        <span>{initials}</span>
      ) : (
        <User className={iconSizes[size]} />
      )}
    </div>
  );
}
